import { LocationType, type MapPoint } from '../types';
import { countryNameToIsoCode } from '../utils/country-codes';
import { type ChoroplethLayerConfig, LAYER_CONFIGS } from './ChoroplethConfig';
import { normalizeName } from './dataManager';

type TooltipFeature = Record<string, unknown> | null;

function mapLocationTypeToNumeric(locationType: LocationType): number {
  if (locationType === LocationType.COUNTRY) {
    return 1;
  }
  if (locationType === LocationType.METRO_AREA) {
    return 2;
  }
  if (locationType === LocationType.CITY) {
    return 3;
  }
  if (locationType === LocationType.STATE) {
    return 4;
  }

  return 0;
}

export function createChoroplethFeatureBuilder(points: MapPoint[]) {
  const keyToIdByLayerFillId: Record<string, Record<string, number>> = {};
  const keyToLabelByLayerFillId: Record<string, Record<string, string>> = {};

  const ensureLayer = (fillId: string) => {
    if (!keyToIdByLayerFillId[fillId]) keyToIdByLayerFillId[fillId] = {};
    if (!keyToLabelByLayerFillId[fillId]) {
      keyToLabelByLayerFillId[fillId] = {};
    }
    return keyToIdByLayerFillId[fillId] as Record<string, number>;
  };

  const findConfigForPoint = (p: MapPoint): ChoroplethLayerConfig | undefined =>
    LAYER_CONFIGS.find((c) => c.isApplicable(p));

  points.forEach((p) => {
    const cfg = findConfigForPoint(p);
    if (!cfg) return;
    const map = ensureLayer(cfg.fillLayerId);
    const titleMap = keyToLabelByLayerFillId[cfg.fillLayerId] as Record<
      string,
      string
    >;
    const labelNorm = normalizeName(p.label);

    let key: string;
    if (cfg.id === LocationType.COUNTRY) {
      const iso = countryNameToIsoCode[labelNorm.toLowerCase()];
      key = iso ?? labelNorm;
    } else if (
      cfg.id === LocationType.METRO_AREA ||
      cfg.id === LocationType.CITY ||
      cfg.id === LocationType.STATE ||
      cfg.id === LocationType.COUNTY
    ) {
      key = String(p.id);
    } else if (cfg.id === LocationType.REGION) {
      key = labelNorm;
    } else {
      key = labelNorm;
    }

    const idNum = Number(p.id);
    if (!Number.isNaN(idNum)) {
      map[key] = idNum;
      if (p.label) titleMap[key] = p.label;
    }
  });

  return (props: Record<string, unknown>, layerId: string): TooltipFeature => {
    const cfg: ChoroplethLayerConfig | undefined = LAYER_CONFIGS.find(
      (c) => c.fillLayerId === layerId
    );
    if (!cfg) return null;

    const t = cfg.id;
    const typeNumeric = mapLocationTypeToNumeric(t);

    if (t === LocationType.METRO_AREA) {
      const idRaw = props['msa_id'];
      const idNum =
        typeof idRaw === 'string' || typeof idRaw === 'number'
          ? Number(idRaw)
          : undefined;
      if (idNum == null || Number.isNaN(idNum)) return null;

      const titleLabel =
        (props['metro_area_name'] as string) ||
        (props['name'] as string) ||
        (props['NAME'] as string) ||
        (props['msa_name'] as string) ||
        '';

      const titleMap = keyToLabelByLayerFillId[layerId] || {};

      return {
        id: idNum,
        type: typeNumeric,
        label: titleMap[String(idNum)] || titleLabel,
        location: titleMap[String(idNum)] || titleLabel,
      };
    }

    if (t === LocationType.CITY) {
      const idRaw = props['city_id'];
      const idNum =
        typeof idRaw === 'string' || typeof idRaw === 'number'
          ? Number(idRaw)
          : undefined;
      if (idNum == null || Number.isNaN(idNum)) return null;

      const titleLabel =
        (props['city_name'] as string) ||
        (props['name'] as string) ||
        (props['NAME'] as string) ||
        '';

      const titleMap = keyToLabelByLayerFillId[layerId] || {};

      return {
        id: idNum,
        type: typeNumeric,
        label: titleMap[String(idNum)] || titleLabel,
        location: titleMap[String(idNum)] || titleLabel,
      };
    }

    if (t === LocationType.STATE) {
      const idRaw = props['state_id'];
      const idNum =
        typeof idRaw === 'string' || typeof idRaw === 'number'
          ? Number(idRaw)
          : undefined;
      if (idNum == null || Number.isNaN(idNum)) return null;

      const titleLabel =
        (props['shapeName'] as string) || (props['name'] as string) || '';

      const titleMap = keyToLabelByLayerFillId[layerId] || {};

      return {
        id: idNum,
        type: typeNumeric,
        label: titleMap[String(idNum)] || titleLabel,
        location: titleMap[String(idNum)] || titleLabel,
      };
    }

    if (t === LocationType.COUNTY) {
      const idRaw = props['county_id'];
      const idNum =
        typeof idRaw === 'string' || typeof idRaw === 'number'
          ? Number(idRaw)
          : undefined;
      if (idNum == null || Number.isNaN(idNum)) return null;

      const titleLabel =
        (props['shapeName'] as string) || (props['name'] as string) || '';

      const titleMap = keyToLabelByLayerFillId[layerId] || {};

      return {
        id: idNum,
        type: typeNumeric,
        label: titleMap[String(idNum)] || titleLabel,
        location: titleMap[String(idNum)] || titleLabel,
      };
    }

    let featureKey = '';
    let titleLabel = '';
    if (t === LocationType.COUNTRY) {
      const isoRaw = props['iso_3166_1'];
      const iso = typeof isoRaw === 'string' ? isoRaw.toUpperCase() : '';
      if (!iso) return null;
      featureKey = iso;
      titleLabel =
        (props['name_en'] as string) || (props['name'] as string) || iso;
    } else if (t === LocationType.REGION) {
      const raw = String(props['name'] || '');
      if (!raw) return null;
      featureKey = normalizeName(raw);
      titleLabel = raw;
    }

    const layerMap = keyToIdByLayerFillId[layerId] || {};
    const idNum = layerMap[featureKey];
    if (idNum == null) return null;

    const titleMap = keyToLabelByLayerFillId[layerId] || {};
    return {
      id: idNum,
      type: typeNumeric,
      label: titleMap[featureKey] || titleLabel,
      location: titleMap[featureKey] || titleLabel,
    };
  };
}
